import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Checkbox,
  Input,
  Option,
  Select,
  Spinner,
  Typography,
} from '@material-tailwind/react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Field } from '../../../../components/common/form/Field';
import { nameRules } from '../../../../constants/validation';
import { useEffect, useState } from 'react';
import {
  applyFreeTrial,
  getFreeTrialQuota,
  getFreeTrialStatus,
} from '../../../../api/utils/free-trial/api';
import {
  BIRTH_YEAR_OPTIONS,
  ApplyFreeTrialRequest,
  FreeTrialApplyFormValues,
  FreeTrialQuotaItem,
  FUNNEL_OPTIONS,
  FunnelTypeEnum,
  StatusEnum,
} from '../../../../constants/types/free-trial/type';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert } from '../../../../store/features/modalSlice';
import { getErrorMessages } from '../../../../api/utils/util-func';
import FreeTrialAttentionEffect from '../../../../components/free-trial/FreeTrialAttentionEffect';

const FreeTrialApply = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const [freeTrialQuota, setFreeTrialQuota] =
    useState<FreeTrialQuotaItem | null>(null);
  const [isCheckingTrialCount, setIsCheckingTrialCount] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchFreeTrialQuota = async () => {
      try {
        setIsCheckingTrialCount(true);

        const quota = await getFreeTrialQuota();
        const status = await getFreeTrialStatus();

        if (status.status === StatusEnum.IN_PROGRESS) {
          dispatch(
            openAlert({
              message: '현재 진행 중인 무료체험이 있습니다.',
            }),
          );

          sessionStorage.setItem('freeTrialLinkToken', status.linkToken);
          navigate('/free-trial/lesson');
          return;
        }

        if (quota.remaining === 0) {
          dispatch(
            openAlert({
              message: '무료체험 신청 가능 횟수를 모두 사용했습니다.',
            }),
          );

          navigate('/');
          return;
        }

        setFreeTrialQuota(quota);
      } catch (error) {
        console.error('무료체험 정보 조회 실패', error);
        dispatch(openAlert({ message: getErrorMessages(error) }));
      } finally {
        setIsCheckingTrialCount(false);
      }
    };

    void fetchFreeTrialQuota();
  }, [navigate]);

  const {
    control,
    handleSubmit,
    watch,
    resetField,
    formState: { errors, isValid },
  } = useForm<FreeTrialApplyFormValues>({
    mode: 'onChange',
    defaultValues: {
      childName: '',
      childGender: '',
      childBirthYear: '',
      funnel: '',
      funnelDetail: '',
      marketingAgreed: false,
    },
  });

  const funnel = watch('funnel');

  const isFunnelEtc = funnel === 'ETC';

  const onSubmit = async (form: FreeTrialApplyFormValues) => {
    if (!form.childGender || !form.funnel) return;

    const request: ApplyFreeTrialRequest = {
      childName: form.childName.trim(),
      childGender: form.childGender,
      childBirthYear: Number(form.childBirthYear),
      funnel: form.funnel,
      funnelDetail:
        form.funnel === 'ETC' ? form.funnelDetail.trim() : undefined,
      marketingAgreed: form.marketingAgreed,
    };

    try {
      setIsLoading(true);

      const data = await applyFreeTrial(request);

      sessionStorage.setItem('freeTrialLinkToken', data.linkToken);

      navigate('/free-trial/complete', {
        state: {
          childName: data.childName,
          expireAt: data.expireAt,
          remainingApplications: data.remainingApplications,
          subjectLabel: data.subjectLabel,
          reused: data.reused,
        },
      });
    } catch (error) {
      console.error('무료체험 신청 가능 횟수 조회 실패', error);
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingTrialCount) {
    return (
      <div className="flex min-h-[calc(100dvh-58px)] items-center justify-center">
        <Typography className="text-sm text-gray-900">
          무료체험 정보를 불러오고 있어요.
        </Typography>
      </div>
    );
  }

  if (!freeTrialQuota) {
    return (
      <div className="flex min-h-[calc(100dvh-58px)] items-center justify-center">
        <div className="text-center">
          <Typography className="text-base font-bold text-gray-900">
            무료체험 정보를 불러오지 못했습니다.
          </Typography>

          <Typography className="mt-2 text-sm text-gray-700">
            잠시 후 다시 시도해주세요.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100dvh-58px)] bg-white px-5 pt-6 xl:bg-gray0">
      <div className="w-full xl:mx-auto xl:w-[580px] xl:px-0 xl:py-[30px]">
        <Card className="mb-[27px] mt-0 rounded-none shadow-none xl:rounded-[20px] xl:shadow-card">
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardBody className="px-0 py-0 xl:px-[32px] xl:pb-0 xl:pt-[32px]">
              {/* 무료체험 정보 */}
              <div className="border-b border-gray-100 pb-5">
                <div className="inline-flex rounded-full bg-[#F1EDFF] px-3 py-1.5">
                  <Typography className="text-[11px] font-bold text-[#6737F4]">
                    잉스바 Book 1 · In the beginning
                  </Typography>
                </div>

                <Typography className="mt-3 text-[24px] font-extrabold leading-tight text-gray-900">
                  무료체험 신청
                </Typography>

                <Typography className="mt-2 text-[13px] font-medium text-gray-700">
                  남은 신청 횟수{' '}
                  <span className="font-bold text-[#6737F4]">{`${freeTrialQuota.limit}회 중 ${freeTrialQuota.remaining ?? 0}회`}</span>
                  <span className="mx-1.5 text-gray-700">·</span>
                  체험 기간 3일
                </Typography>
              </div>

              <div className="mt-6 flex flex-col gap-1 xl:gap-2">
                {/* 이름 */}
                <Controller
                  name="childName"
                  control={control}
                  rules={nameRules({
                    label: '자녀 이름',
                    required: true,
                  })}
                  render={({ field }) => (
                    <Field label="자녀 이름" error={errors.childName?.message}>
                      <Input
                        {...field}
                        placeholder="예) 김젭스"
                        labelProps={{
                          className: 'hidden',
                        }}
                        containerProps={{
                          className: 'min-w-0',
                        }}
                        className={`!h-10 !rounded-xl bg-white px-4 text-sm placeholder:text-gray-400 ${
                          errors.childName
                            ? '!border-red-500 focus:!border-red-500'
                            : '!border-gray-200 focus:!border-[#6737F4]'
                        }`}
                      />
                    </Field>
                  )}
                />

                {/* 성별 */}
                <Controller
                  name="childGender"
                  control={control}
                  rules={{
                    required: '자녀 성별을 선택해주세요.',
                  }}
                  render={({ field }) => (
                    <Field
                      label="자녀 성별"
                      error={errors.childGender?.message}
                    >
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          ripple={false}
                          variant="outlined"
                          onClick={() => field.onChange('MALE')}
                          className={`h-10 rounded-xl py-0 text-sm font-bold normal-case shadow-none hover:shadow-none focus:shadow-none ${
                            field.value === 'MALE'
                              ? 'border-[#6737F4] bg-[#F4F0FF] text-[#6737F4]'
                              : errors.childGender
                                ? 'border-red-500 bg-white text-gray-700'
                                : 'border-gray-200 bg-white text-gray-700'
                          }`}
                        >
                          남자
                        </Button>

                        <Button
                          type="button"
                          ripple={false}
                          variant="outlined"
                          onClick={() => field.onChange('FEMALE')}
                          className={`h-10 rounded-xl py-0 text-sm font-bold normal-case shadow-none hover:shadow-none focus:shadow-none ${
                            field.value === 'FEMALE'
                              ? 'border-[#6737F4] bg-[#F4F0FF] text-[#6737F4]'
                              : errors.childGender
                                ? 'border-red-500 bg-white text-gray-700'
                                : 'border-gray-200 bg-white text-gray-700'
                          }`}
                        >
                          여자
                        </Button>
                      </div>
                    </Field>
                  )}
                />

                {/* 출생년도 */}
                <Controller
                  name="childBirthYear"
                  control={control}
                  rules={{
                    required: '자녀 출생년도를 선택해주세요.',
                  }}
                  render={({ field }) => (
                    <Field
                      label="자녀 출생년도"
                      error={errors.childBirthYear?.message}
                    >
                      <div className="relative">
                        <Select
                          value={field.value}
                          onChange={(value) => field.onChange(value ?? '')}
                          className={`!h-10 !rounded-xl px-4 text-sm text-gray-700 outline-none transition-colors ${
                            errors.childBirthYear
                              ? '!border-red-500 focus:!border-red-500'
                              : '!border-gray-200 focus:!border-[#6737F4]'
                          }`}
                          labelProps={{
                            className: 'before:content-none after:content-none',
                          }}
                          containerProps={{
                            className: 'h-10 min-w-0',
                          }}
                        >
                          {BIRTH_YEAR_OPTIONS.map((item) => (
                            <Option key={item.value} value={String(item.value)}>
                              {item.label}
                            </Option>
                          ))}
                        </Select>

                        {!field.value && (
                          <span className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-sm font-normal text-gray-400">
                            선택해 주세요
                          </span>
                        )}
                      </div>
                    </Field>
                  )}
                />

                {/* 유입 경로 */}
                <Controller
                  name="funnel"
                  control={control}
                  rules={{
                    required: '젭스를 알게 된 경로를 선택해주세요.',
                  }}
                  render={({ field }) => (
                    <Field
                      label="젭스를 어떻게 알게 되셨나요?"
                      error={errors.funnel?.message}
                    >
                      <div className="flex flex-wrap gap-2">
                        {FUNNEL_OPTIONS.map((option) => {
                          const isSelected = field.value === option.value;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                field.onChange(option.value);

                                // 기타 → 다른 항목으로 변경하면 입력값 초기화
                                if (option.value !== FunnelTypeEnum.ETC) {
                                  resetField('funnelDetail');
                                }
                              }}
                              className={`rounded-full border px-3 py-2 text-[12px] font-semibold transition-colors ${
                                isSelected
                                  ? 'border-[#6737F4] bg-[#F4F0FF] text-[#6737F4]'
                                  : errors.funnel
                                    ? 'border-red-300 bg-white text-gray-700'
                                    : 'border-gray-200 bg-white text-gray-700'
                              }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  )}
                />

                {/* 기타 상세 입력 */}
                {isFunnelEtc && (
                  <Controller
                    name="funnelDetail"
                    control={control}
                    rules={{
                      required: '유입 경로를 직접 입력해주세요.',
                      maxLength: {
                        value: 100,
                        message: '유입 경로는 100자 이하로 입력해주세요.',
                      },
                      validate: (value) =>
                        value.trim().length > 0 ||
                        '유입 경로를 직접 입력해주세요.',
                    }}
                    render={({ field }) => (
                      <Field
                        label="기타 유입 경로"
                        error={errors.funnelDetail?.message}
                      >
                        <Input
                          {...field}
                          maxLength={100}
                          placeholder="예) 교회 소개 등"
                          labelProps={{
                            className: 'hidden',
                          }}
                          containerProps={{
                            className: 'min-w-0',
                          }}
                          className={`!h-10 !rounded-xl bg-white px-4 text-sm placeholder:text-gray-400 ${
                            errors.funnelDetail
                              ? '!border-red-500 focus:!border-red-500'
                              : '!border-gray-200 focus:!border-[#6737F4]'
                          }`}
                        />
                      </Field>
                    )}
                  />
                )}

                {/* 선택 동의 */}
                <Controller
                  name="marketingAgreed"
                  control={control}
                  render={({ field }) => (
                    <div className="flex flex-col gap-2 rounded-xl bg-[#F7F5FC] px-3 py-3">
                      <div className="mt-1 flex items-center">
                        <Checkbox
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          color="deep-purple"
                          className="h-4 w-4 rounded"
                          containerProps={{
                            className: 'p-1',
                          }}
                        />

                        <Typography className="ml-1 text-[12px] font-medium text-gray-700">
                          [선택] 젭스 이벤트·혜택 소식을 전화·문자로 받겠습니다.
                        </Typography>
                      </div>
                    </div>
                  )}
                />
              </div>
            </CardBody>

            <CardFooter className="px-0 pb-0 pt-8 xl:px-[32px] xl:pb-[32px] xl:pt-6">
              <Button
                type="submit"
                ripple={false}
                disabled={!isValid || isLoading}
                className={`relative flex h-12 w-full items-center justify-center gap-2 rounded-xl py-0 text-[15px] font-bold normal-case shadow-none focus:shadow-none ${
                  isValid && !isLoading
                    ? 'bg-[#6737F4] text-white hover:bg-[#5D2EE7] hover:shadow-none'
                    : 'cursor-not-allowed bg-gray-200 text-gray-400'
                }`}
              >
                {isValid && !isLoading && <FreeTrialAttentionEffect />}

                <span className="relative z-10 flex items-center gap-2">
                  {isLoading && <Spinner className="h-4 w-4" />}
                  무료체험 시작하기
                </span>
              </Button>

              <Typography className="mt-3 text-center text-[11px] font-normal text-gray-700">
                체험 링크는 가입하신 휴대폰 번호로 알림톡으로 보내 드려요
              </Typography>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default FreeTrialApply;
